import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchGuestBooking, fetchGuestUpcoming } from '../../api/guestApi';
import { useToast } from '../../components/ToastContext';

const formatDate = (value) => {
  if (!value) return 'TBD';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
};

const formatAmount = (currency, amount) => {
  const numeric = Number(amount);
  if (Number.isNaN(numeric)) return `${currency || ''} ${amount || ''}`.trim();
  return `${currency || ''} ${numeric.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`.trim();
};

const getNights = (checkIn, checkOut) => {
  if (!checkIn || !checkOut) return null;
  const start = new Date(checkIn);
  const end = new Date(checkOut);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : null;
};

const getDaysLeft = (checkIn) => {
  if (!checkIn) return null;
  const start = new Date(checkIn);
  if (Number.isNaN(start.getTime())) return null;
  const diff = Math.ceil((start - new Date()) / (1000 * 60 * 60 * 24));
  if (diff <= 0) return 'Arrives today';
  return `${diff} days to go`;
};

const resolveImage = (src) => {
  if (!src) return '';
  if (src.startsWith('http')) return src;
  const base = process.env.REACT_APP_API_BASE_URL || '';
  return `${base}${src.startsWith('/') ? src : `/${src}`}`;
};

const badgeStyles = {
  confirmed: 'bg-emerald-100 text-emerald-700',
  pending: 'bg-amber-100 text-amber-700',
  awaiting_payment: 'bg-amber-100 text-amber-700',
  cancelled: 'bg-rose-100 text-rose-700',
  paid: 'bg-emerald-100 text-emerald-700',
  requires_payment: 'bg-amber-100 text-amber-700',
  partial: 'bg-sky-100 text-sky-700',
  failed: 'bg-rose-100 text-rose-700',
  refunded: 'bg-rose-100 text-rose-700',
  default: 'bg-slate-100 text-slate-700',
};

const StatusBadge = ({ value }) => {
  const key = String(value || '').toLowerCase();
  const cls = badgeStyles[key] || badgeStyles.default;
  const label = String(value || 'unknown').replace(/_/g, ' ');
  return <span className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${cls}`}>{label}</span>;
};

const GuestUpcoming = () => {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0, limit: 6 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async (nextPage = page) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchGuestUpcoming({ page: nextPage, limit: pagination.limit || 6 });
      setItems(res.data || []);
      setPagination(res.pagination || { page: nextPage, totalPages: 1, total: 0, limit: pagination.limit || 6 });
    } catch (err) {
      setError(err.message || 'Unable to load upcoming stays');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const openDetail = async (booking) => {
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const detail = await fetchGuestBooking(booking.id);
      setSelected({ ...detail, host: booking.host || null });
    } catch (err) {
      toast.error(err.message || 'Unable to load booking detail');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelected(null);
  };

  const pageSummary = useMemo(() => {
    const start = (pagination.page - 1) * pagination.limit + 1;
    const end = Math.min(pagination.page * pagination.limit, pagination.total || 0);
    if (!pagination.total) return '0 results';
    return `${start}-${end} of ${pagination.total}`;
  }, [pagination]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">Upcoming stays</p>
          <h2 className="text-2xl font-semibold text-slate-900">Your next reservations</h2>
        </div>
        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600">{pageSummary}</span>
      </div>

      {error && <div className="rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}
      {loading && <div className="text-sm text-slate-500">Loading upcoming stays...</div>}

      {!loading && !error && items.length === 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-10 text-center text-slate-500">
          Your upcoming reservations will appear here.
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="grid gap-4 lg:grid-cols-2">
          {items.map((b) => {
            const nights = getNights(b.check_in_date, b.check_out_date);
            const daysLeft = getDaysLeft(b.check_in_date);
            return (
              <div key={b.id} className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="flex flex-col md:flex-row">
                  <div className="md:w-48 h-44 md:h-auto bg-slate-100">
                    {b.property_image ? (
                      <img
                        src={resolveImage(b.property_image)}
                        alt={b.property_title || 'Property'}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-xs text-slate-400">
                        No image
                      </div>
                    )}
                  </div>
                  <div className="flex-1 p-4 space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="text-xs uppercase tracking-wide text-slate-400">Booking</p>
                        <p className="text-lg font-semibold text-slate-900">{b.booking_code}</p>
                      </div>
                      <div className="flex gap-2">
                        <StatusBadge value={b.booking_status} />
                        <StatusBadge value={b.payment_status} />
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-semibold text-slate-900">{b.property_title || 'Property'}</p>
                      <p className="text-xs text-slate-500">{b.property_location || 'Location TBD'}</p>
                      {b.host?.name && (
                        <p className="mt-1 text-xs text-slate-500">Hosted by {b.host.name}</p>
                      )}
                    </div>

                    <div className="grid gap-3 text-sm text-slate-700 sm:grid-cols-2">
                      <div>
                        <p className="text-xs text-slate-400">Check-in</p>
                        <p className="font-semibold">{formatDate(b.check_in_date)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Check-out</p>
                        <p className="font-semibold">{formatDate(b.check_out_date)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Nights</p>
                        <p className="font-semibold">{nights || 'TBD'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Countdown</p>
                        <p className="font-semibold text-emerald-700">{daysLeft || 'TBD'}</p>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-slate-900">{formatAmount(b.currency, b.total_amount)}</p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => openDetail(b)}
                          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-emerald-200 hover:text-emerald-700"
                        >
                          View details
                        </button>
                        <Link
                          to={`/guest/messages?bookingId=${b.id}${b.host?.id ? `&hostId=${b.host.id}` : ''}`}
                          className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white"
                        >
                          Message host
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1}
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 disabled:opacity-50"
        >
          Previous
        </button>
        <div className="text-sm text-slate-600">
          Page {pagination.page} of {pagination.totalPages}
        </div>
        <button
          type="button"
          onClick={() => setPage((p) => Math.min(pagination.totalPages || 1, p + 1))}
          disabled={page >= pagination.totalPages}
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 disabled:opacity-50"
        >
          Next
        </button>
      </div>

      {detailOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6">
          <button
            type="button"
            onClick={closeDetail}
            className="absolute inset-0 bg-slate-950/60"
            aria-label="Close booking detail"
          />
          <div className="relative w-full max-w-3xl rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-400">Booking detail</p>
                <p className="text-lg font-semibold text-slate-900">{selected?.booking_code || 'Booking'}</p>
              </div>
              <button
                type="button"
                onClick={closeDetail}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600"
              >
                Close
              </button>
            </div>

            {detailLoading && <div className="px-5 py-6 text-sm text-slate-500">Loading details...</div>}

            {!detailLoading && selected && (
              <div className="grid gap-5 px-5 py-6 md:grid-cols-2">
                <div className="space-y-4">
                  <div className="rounded-xl border border-slate-200 p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-400">Property</p>
                    <p className="mt-2 text-lg font-semibold text-slate-900">{selected.property?.title || 'Property'}</p>
                    <p className="text-sm text-slate-600">{selected.property?.location || 'Location TBD'}</p>
                    {selected.property?.image_url && (
                      <img
                        src={resolveImage(selected.property.image_url)}
                        alt={selected.property.title || 'Property'}
                        className="mt-3 h-40 w-full rounded-xl object-cover"
                      />
                    )}
                  </div>
                  <div className="rounded-xl border border-slate-200 p-4 text-sm text-slate-700 space-y-2">
                    <div>
                      <p className="text-xs text-slate-400">Check-in</p>
                      <p className="font-semibold">{formatDate(selected.check_in_date)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Check-out</p>
                      <p className="font-semibold">{formatDate(selected.check_out_date)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Total</p>
                      <p className="font-semibold">{formatAmount(selected.currency, selected.total_amount)}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="rounded-xl border border-slate-200 p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-400">Status</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <StatusBadge value={selected.booking_status} />
                      <StatusBadge value={selected.payment_status} />
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-400">Host</p>
                    <div className="mt-3 text-sm text-slate-700 space-y-1">
                      <p className="font-semibold">{selected.host?.name || 'Host'}</p>
                      {selected.host?.email && <p>{selected.host.email}</p>}
                      {selected.host?.phone && <p>{selected.host.phone}</p>}
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-400">Actions</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Link
                        to={`/guest/messages?bookingId=${selected.id}${selected.host?.id ? `&hostId=${selected.host.id}` : ''}`}
                        className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white"
                      >
                        Message host
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GuestUpcoming;

